import React, { useEffect, useState, Component } from 'react';
import { ArrowLeft, Clock, MapPin, Coffee, CreditCard, Gift, Heart, Sparkles, Users, Share2, Check, UserPlus, Copy, X, Utensils } from 'lucide-react';
import { Store } from '../../types';
import { useToast } from '../ToastContext';
interface CheckInSuccessScreenProps {
  store: Store;
  onDismiss: () => void;
  onViewMenu?: () => void;
}
export function CheckInSuccessScreen({
  store,
  onDismiss,
  onViewMenu
}: CheckInSuccessScreenProps) {
  const [showInitialCard, setShowInitialCard] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(true);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralCode, setReferralCode] = useState<string>('');
  const {
    showToast
  } = useToast();
  // Generate referral code
  const generateReferralCode = () => {
    const storePrefix = store.name.substring(0, 4).toUpperCase().replace(/\s/g, '');
    const randomHash = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `${storePrefix}-${randomHash}`;
  };
  const handleRecommend = () => {
    const code = generateReferralCode();
    setReferralCode(code);
    setShowReferralModal(true);
  };
  const handleCopyCode = () => {
    navigator.clipboard.writeText(referralCode);
    showToast('Referral code copied!', 'success');
  };
  const handleShareWhatsApp = () => {
    const message = `Hey! Check out ${store.name}! Use my code ${referralCode} and we both get ₹100 credit! 🎁`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };
  // Flip to membership card after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);
  return <div className="flex flex-col h-full bg-[#B353F2] overflow-y-auto" data-id="element-219">
      {/* Header */}
      <div className="relative" data-id="element-220">
        {/* Back Button */}
        <button onClick={onDismiss} className="absolute top-6 left-4 z-10 bg-[#FFFC00] rounded-full p-3 shadow-lg hover:bg-[#F7C300] transition-colors" data-id="element-221">
          <ArrowLeft size={24} className="text-black" data-id="element-222" />
        </button>

        {/* Distance Badge */}
        <div className="absolute top-6 right-4 z-10" data-id="element-223">
          <div className="flex items-center bg-black/20 backdrop-blur-sm text-white px-3 py-1.5 rounded-full" data-id="element-224">
            <MapPin size={16} className="mr-1" data-id="element-225" />
            <span className="text-sm font-medium" data-id="element-226">{store.distance}</span>
          </div>
        </div>

        {/* Store Image with Time Overlay */}
        <div className="w-full h-48 relative" data-id="element-227">
          <img src={store.image} alt={store.name} className="w-full h-full object-cover" data-id="element-228" />
          <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white" data-id="element-229">
            <div className="flex items-center gap-3 mb-2" data-id="element-230">
              <Clock size={24} className="text-white" data-id="element-231" />
              <span className="text-3xl font-light" data-id="element-232">{store.hours}</span>
            </div>
            <span className="text-xl font-light" data-id="element-233">Open Now</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-5 py-6 pb-32" data-id="element-234">
        {/* Store Info */}
        <div className="mb-6" data-id="element-235">
          <h1 className="text-4xl font-bold text-white mb-3" data-id="element-236">{store.name}</h1>
          <div className="flex gap-2 flex-wrap" data-id="element-237">
            {store.tags.map(tag => <span key={tag} className="bg-white text-black px-4 py-1.5 rounded-full text-sm font-medium" data-id="element-238">
                {tag}
              </span>)}
          </div>
        </div>

        {/* Flip Card: Success Message → Membership Card */}
        <div className="relative h-32 mb-6" style={{
        perspective: '1000px'
      }} data-id="element-239">
          <div className="relative w-full h-full transition-all duration-700 ease-in-out" style={{
          transformStyle: 'preserve-3d',
          transform: showSuccessMessage ? 'rotateY(0deg)' : 'rotateY(180deg)'
        }} data-id="element-240">
            {/* Front: Success Message */}
            <div className="absolute inset-0 bg-[#E6FFE6] rounded-xl p-4 animate-in fade-in slide-in-from-top duration-500" style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden'
          }} data-id="element-241">
              <div className="flex items-start h-full" data-id="element-242">
                <div className="w-6 h-6 rounded-full bg-[#4CD964] flex items-center justify-center mr-3 mt-0.5 flex-shrink-0" data-id="element-243">
                  <Check size={14} className="text-white" strokeWidth={3} data-id="element-244" />
                </div>
                <div data-id="element-245">
                  <h2 className="text-lg font-semibold text-[#4CD964] mb-1" data-id="element-246">
                    Check-in successful!
                  </h2>
                  <p className="text-[#4CD964]" data-id="element-247">
                    You're now being tracked for rewards
                  </p>
                </div>
              </div>
            </div>

            {/* Back: Membership Card */}
            <div className="absolute inset-0 bg-gradient-to-br from-red-900 via-red-600 to-amber-500 rounded-xl p-4" style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)'
          }} data-id="element-248">
              <div className="h-full flex flex-col justify-between" data-id="element-249">
                <div data-id="element-250">
                  <div className="flex items-center gap-2 mb-2" data-id="element-251">
                    <div className="text-2xl" data-id="element-252">👑</div>
                    <div data-id="element-253">
                      <h3 className="text-amber-200 font-bold text-base leading-tight" data-id="element-254">
                        MAHARAJA MEMBERSHIP
                      </h3>
                      <p className="text-amber-100/80 text-xs" data-id="element-255">
                        Royal treatment, every visit
                      </p>
                    </div>
                  </div>
                </div>
                <button onClick={() => showToast('Maharaja Membership coming soon! 👑', 'info')} className="w-full bg-gradient-to-r from-amber-400 to-yellow-300 text-red-900 py-2.5 rounded-lg font-bold text-sm hover:from-amber-300 hover:to-yellow-200 transition-all active:scale-95 shadow-lg" data-id="element-256">
                  Join the Royal Circle - ₹999/year
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* View Menu & Order - Always Visible Primary CTA */}
        {onViewMenu && <button onClick={onViewMenu} className="w-full bg-[#FFFC00] text-black py-4 rounded-xl font-bold text-base hover:bg-[#F7C300] transition-all active:scale-[0.98] shadow-lg flex items-center justify-center gap-2 mb-4" data-id="element-257">
            <Utensils size={20} data-id="element-258" />
            View Menu & Order
          </button>}

        {/* STAGE 1: How It Works Card */}
        {showInitialCard ? <div className="bg-[#FFFC00] rounded-xl p-5 animate-in fade-in slide-in-from-bottom duration-500" data-id="element-259">
            <h2 className="text-lg font-bold mb-4 text-black" data-id="element-260">How It Works</h2>

            {/* Three Steps */}
            <div className="space-y-4 mb-6" data-id="element-261">
              <div className="flex items-start" data-id="element-262">
                <Coffee size={20} className="text-black mr-3 mt-0.5 flex-shrink-0" data-id="element-263" />
                <p className="text-sm text-black" data-id="element-264">
                  Order from the menu in the app like you normally would
                </p>
              </div>
              <div className="flex items-start" data-id="element-265">
                <CreditCard size={20} className="text-black mr-3 mt-0.5 flex-shrink-0" data-id="element-266" />
                <p className="text-sm text-black" data-id="element-267">
                  Pay using your linked card or UPI through our app
                </p>
              </div>
              <div className="flex items-start" data-id="element-268">
                <Gift size={20} className="text-black mr-3 mt-0.5 flex-shrink-0" data-id="element-269" />
                <p className="text-sm text-black" data-id="element-270">
                  If a reward is active, it'll get added automatically after
                  payment
                </p>
              </div>
            </div>

            {/* Encouragement Text */}
            <p className="text-center text-black/70 text-sm italic mb-4" data-id="element-271">
              Now sit back & relax, enjoy your visit!
            </p>

            {/* Got It Button */}
            <button onClick={() => setShowInitialCard(false)} className="w-full bg-black text-white py-3.5 rounded-xl font-medium hover:bg-black/90 transition-all transform hover:scale-[1.02] active:scale-[0.98] duration-200" data-id="element-272">
              Got it
            </button>
          </div> /* STAGE 2: Action Cards After "Got it" */ : <div className="space-y-4 animate-in fade-in slide-in-from-bottom duration-500" data-id="element-273">
            {/* Quick Order Favorites */}
            <div className="bg-[#F0EEFF] rounded-xl p-4 hover:bg-[#E5D9FF] transition-colors cursor-pointer group" onClick={() => showToast("Quick orders coming soon! We'll keep you posted 🚀", 'info')} data-id="element-274">
              <div className="flex items-center justify-between" data-id="element-275">
                <div className="flex items-center flex-1" data-id="element-276">
                  <div className="w-10 h-10 rounded-full bg-[#FFFC00] flex items-center justify-center mr-3 flex-shrink-0" data-id="element-277">
                    <Heart size={20} className="text-black" data-id="element-278" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-279">
                    <h3 className="font-semibold text-black" data-id="element-280">
                      Quick Order Favorites
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-281">
                      Your usual: Latte, Croissant
                    </p>
                  </div>
                </div>
                <span className="text-[#A020F0] text-sm font-medium group-hover:translate-x-1 transition-transform ml-2 flex-shrink-0" data-id="element-282">
                  Order →
                </span>
              </div>
            </div>

            {/* Today's Specials */}
            <div className="bg-[#FFF3E0] rounded-xl p-4 hover:bg-[#FFE0B2] transition-colors cursor-pointer group" onClick={() => showToast("Today's specials coming soon! Stay tuned 🎉", 'info')} data-id="element-283">
              <div className="flex items-center justify-between" data-id="element-284">
                <div className="flex items-center flex-1" data-id="element-285">
                  <div className="w-10 h-10 rounded-full bg-[#FF9800] flex items-center justify-center mr-3 flex-shrink-0" data-id="element-286">
                    <Sparkles size={20} className="text-white" data-id="element-287" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-288">
                    <h3 className="font-semibold text-black" data-id="element-289">
                      Today's Specials
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-290">2 new items added</p>
                  </div>
                </div>
                <span className="text-[#FF9800] text-sm font-medium group-hover:translate-x-1 transition-transform ml-2 flex-shrink-0" data-id="element-291">
                  View →
                </span>
              </div>
            </div>

            {/* Skip the Line */}
            <div className="bg-[#E8F5E9] rounded-xl p-4 hover:bg-[#C8E6C9] transition-colors cursor-pointer group" onClick={() => showToast("Skip the line feature coming soon! We'll notify you 🔔", 'info')} data-id="element-292">
              <div className="flex items-center justify-between" data-id="element-293">
                <div className="flex items-center flex-1" data-id="element-294">
                  <div className="w-10 h-10 rounded-full bg-[#4CAF50] flex items-center justify-center mr-3 flex-shrink-0" data-id="element-295">
                    <Users size={20} className="text-white" data-id="element-296" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-297">
                    <h3 className="font-semibold text-black" data-id="element-298">Skip the line</h3>
                    <p className="text-sm text-gray-600" data-id="element-299">Is it too crowded?</p>
                  </div>
                </div>
                <span className="text-[#4CAF50] text-sm font-medium group-hover:translate-x-1 transition-transform ml-2 flex-shrink-0" data-id="element-300">
                  Skip →
                </span>
              </div>
            </div>

            {/* Share Check-in */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl p-4 hover:from-indigo-600 hover:to-purple-600 transition-colors cursor-pointer group" onClick={handleRecommend} data-id="element-301">
              <div className="flex items-center justify-between" data-id="element-302">
                <div className="flex items-center flex-1" data-id="element-303">
                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mr-3 flex-shrink-0" data-id="element-304">
                    <UserPlus size={20} className="text-white" data-id="element-305" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-306">
                    <h3 className="font-semibold text-white" data-id="element-307">
                      Recommend this place
                    </h3>
                    <p className="text-xs text-white/80" data-id="element-308">
                      Share the love with friends, both get ₹100 credit
                    </p>
                  </div>
                </div>
                <span className="text-white text-sm font-medium group-hover:translate-x-1 transition-transform ml-2 flex-shrink-0" data-id="element-309">
                  Refer →
                </span>
              </div>
            </div>

            {/* Done Button */}
            <button onClick={onDismiss} className="w-full bg-[#FFFC00] text-black py-3.5 rounded-xl font-semibold hover:bg-[#F7C300] transition-all transform hover:scale-[1.02] active:scale-[0.98] duration-200 mt-6" data-id="element-310">
              Done
            </button>
          </div>}
      </div>

      {/* Referral Modal */}
      {showReferralModal && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setShowReferralModal(false)} data-id="element-311">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl animate-in slide-in-from-bottom duration-300" onClick={e => e.stopPropagation()} data-id="element-312">
            {/* Header */}
            <div className="flex items-center justify-between mb-4" data-id="element-313">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2" data-id="element-314">
                <Gift size={24} className="text-indigo-600" data-id="element-315" />
                Share & Earn Together!
              </h3>
              <button onClick={() => setShowReferralModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-316">
                <X size={20} className="text-gray-600" data-id="element-317" />
              </button>
            </div>

            {/* Referral Code */}
            <div className="mb-6" data-id="element-318">
              <p className="text-sm text-gray-600 mb-2" data-id="element-319">Your Referral Code:</p>
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-4 flex items-center justify-between" data-id="element-320">
                <span className="font-mono font-bold text-2xl text-indigo-600" data-id="element-321">
                  {referralCode}
                </span>
                <button onClick={handleCopyCode} className="p-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all active:scale-95" title="Copy code" data-id="element-322">
                  <Copy size={18} data-id="element-323" />
                </button>
              </div>
            </div>

            {/* Perks */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 mb-6" data-id="element-324">
              <p className="text-sm font-semibold text-gray-900 mb-3" data-id="element-325">
                When your friend uses this code:
              </p>
              <div className="space-y-2 text-sm text-gray-700" data-id="element-326">
                <div className="flex items-start gap-2" data-id="element-327">
                  <Check size={16} className="text-indigo-600 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-328" />
                  <span data-id="element-329">
                    You get <strong data-id="element-330">₹100 store credit</strong>
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-331">
                  <Check size={16} className="text-indigo-600 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-332" />
                  <span data-id="element-333">
                    They get <strong data-id="element-334">₹50 welcome credit</strong>
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-335">
                  <Check size={16} className="text-indigo-600 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-336" />
                  <span data-id="element-337">
                    Both earn <strong data-id="element-338">bonus loyalty stamps</strong>
                  </span>
                </div>
              </div>
            </div>

            {/* Share Options */}
            <div className="space-y-3" data-id="element-339">
              <p className="text-sm font-semibold text-gray-900" data-id="element-340">Share via:</p>
              <button onClick={handleShareWhatsApp} className="w-full bg-[#25D366] text-white py-3 rounded-xl font-semibold hover:bg-[#20BA5A] transition-all active:scale-[0.98] flex items-center justify-center gap-2" data-id="element-341">
                <Share2 size={18} data-id="element-342" />
                WhatsApp
              </button>
              <button onClick={handleCopyCode} className="w-full bg-gray-100 text-gray-900 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all active:scale-[0.98] flex items-center justify-center gap-2" data-id="element-343">
                <Copy size={18} data-id="element-344" />
                Copy Code
              </button>
            </div>

            {/* Close Button */}
            <button onClick={() => setShowReferralModal(false)} className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all active:scale-[0.98] mt-4" data-id="element-345">
              Done
            </button>
          </div>
        </div>}
    </div>;
}